<template lang="html">
  <div class="mb-3">
    <div class="form-group m-0" v-if="valueType === 'typeahead_list'">
      <label>{{ attr.name }} <sup v-if="required" class="required-tip">*</sup></label>
      <VueBootstrapTypeahead
        :data="suggestions || []"
        v-model="value"
        :initialValue="initialValue"
        :serializer="a => a.name"
        :minMatchingChars="0"
        :disabled="disableInput"
        :isFetching="isFetching"
        placeholder="Buscar"
        @input="checkInput"
        @hit="handleHit"
        @focus="handleTypeaheadFocus"
        @blur="$emit('blur')" />
      <small v-if="(required && checkRequiredFields && !validValue)" class="text-danger">
        Este campo es obligatorio
      </small>
    </div>
    <div class="form-group m-0" v-else-if="valueType === 'number'">
      <label>{{ attr.name }} <sup v-if="required" class="required-tip">*</sup></label>
      <input
        @focus="$emit('focus')"
        @blur="$emit('blur')"
        @input="checkInput"
        :disabled="disableInput"
        class="form-control"
        v-model.number="value"
        type="number">
      <small v-if="(required && checkRequiredFields && !validValue)" class="text-danger">
        Este campo es obligatorio
      </small>
    </div>
    <div class="form-group m-0" v-else-if="valueType === 'number_unit'">
      <label>{{ attr.name }} <sup v-if="required" class="required-tip">*</sup></label>
      <div class="input-group">
        <input
          @focus="$emit('focus')"
          @blur="$emit('blur')"
          @input="checkInput"
          :disabled="disableInput"
          type="number"
          class="form-control"
          v-model="value">
        <div v-if="Array.isArray(attr.allowed_units)" class="input-group-append">
          <select
            @focus="$emit('focus')"
            @blur="$emit('blur')"
            :disabled="disableInput"
            v-model="unitId"
            class="form-control">
            <template v-for="(unit, index) in attr.allowed_units" >
              <option
                :selected="index === 0"
                :key="attr.id + index"
                :value="unit.id">
                {{ unit.name }}
              </option>
            </template>
          </select>
        </div>
      </div>
      <small v-if="(required && checkRequiredFields && (!validValue || !unitId))" class="text-danger">
        Este campo es obligatorio
      </small>
    </div>
    <small v-if="inAttrWithErrors" class="text-danger">
      Valor no válido
    </small>
  </div>
</template>

<script>
import store from '@/store'
// vue-bootstrap-typeahead component
import VueBootstrapTypeahead from '@/components/ui/forms/VueBootstrapTypeahead'
import {isRequiredAttribute, checkAttributeValidity} from '@/helpers'

export default {
  props: {
    attr: Object,
    index: Number,
    isEditingPub: Boolean
  },

  components: {
    VueBootstrapTypeahead
  },

  data: () => ({
    valueId: null,
    value: null,
    unitId: null,
    isFetching: false,
    initialValue: null,
    initializing: false
  }),

  computed: {
    requiredAttributes: () => store.getters['sell/required'],

    prevAttribute () { return this.index > 0 && this.requiredAttributes[this.index - 1] },

    prevAttributeSetValue () { return this.index > 0 && this.prevAttribute.setted_value },

    prevAttributeSetValueId () { return this.index > 0 && this.prevAttribute.setted_value_id },

    validValue () { return !!this.value || this.value === 0 },

    valueType () {
      const type = this.attr.value_type

      if (this.required && this.isPredictable) return 'typeahead_list'
      else {
        if (type === 'number') return 'number'
        else if (type === 'number_unit') return 'number_unit'
        else return 'typeahead_list'
      }
    },

    isPredictable () {
      const filterSug = this.attributeSuggestions.filter(a => a.attribute === this.attr.id)
      const currentAttrSuggestions = filterSug.length > 0 && filterSug[0].suggested

      return !!(this.index === 0 && !currentAttrSuggestions ||
                this.index === 0 && currentAttrSuggestions && currentAttrSuggestions.length > 0 ||
                this.prevAttributeSetValue && this.prevAttributeSetValueId && currentAttrSuggestions && currentAttrSuggestions.length > 0 ||
                !!this.attr.setted_value && !!this.attr.setted_value_id && this.attr.setted_value !== this.attr.setted_value_id)
    },

    required () { return isRequiredAttribute(this.attr) },

    fixed () { return this.attr.tags && this.attr.tags.fixed },

    valueGroup () { return `${this.value} ${this.unitId} ${this.valueId}` },

    checkRequiredFields: () => store.state.sell.checkRequiredFields,

    attributeSuggestions: () => store.state.sell.attributeSuggestions,

    suggestions () {
      if (this.disableInput || this.isFetching) return null
      else {
        const attrSug = this.attributeSuggestions.filter(a => a.attribute === this.attr.id)
        if (attrSug.length > 0 && attrSug[0].suggested.length > 0) {
          return attrSug[0].suggested
        } else {
          return this.attr.values
        }
      }
    },

    disableInput () {
      const emptyNumberUnit = this.prevAttribute.value_type === 'number_unit' && this.prevAttributeSetValue && isNaN(this.prevAttributeSetValue.split(' ')[0])
      return this.index > 0 && (!this.prevAttributeSetValue && this.prevAttributeSetValue !== 0 || emptyNumberUnit)
    },

    attrWithErrors: () => store.state.sell.attrWithErrors,

    inAttrWithErrors () { return this.attrWithErrors.includes(this.attr.id) }
  },

  watch: {
    valueGroup () {
      if (!this.initializing) {
        if (this.valueType === 'typeahead_list') {
          const attrSug = this.attributeSuggestions.filter(a => a.attribute === this.attr.id)
          const currentSuggestions = attrSug.length > 0 && attrSug[0].suggested
          let matches = []
          if (currentSuggestions && currentSuggestions.length > 0) {
            matches = currentSuggestions.filter(s => s.name && this.value && s.name.toLowerCase() === this.value.toLowerCase())
          } else if (this.attr.values) {
            matches = this.attr.values.filter(v => v.name && this.value && v.name.toLowerCase() === this.value.toLowerCase())
          }
          if (matches.length === 0) {
            this.valueId = null
          } else {
            this.handleHit(matches[0])
          }
        }

        let valueId = this.valueId

        let value = this.valueType === 'number_unit'
          ? this.validValue ? this.value + ' ' + this.unitId : this.value
          : this.value

        if (this.valueType === 'number') {
          value = this.value
          valueId = this.value
        }

        const attr = {
          ...this.attr,
          setted_value_id: valueId,
          setted_value: value
        }
        store.dispatch('sell/updateAttribute', attr)
      }
    },

    prevAttributeSetValue () {
      // If previous attr value changes, reset current attr value
      if (this.validValue || this.valueId) {
        this.value = null
        this.valueId = null
        this.initialValue = ''

        const attr = {
          ...this.attr,
          setted_value_id: '',
          setted_value: ''
        }
        store.dispatch('sell/updateAttribute', attr)
        store.dispatch('sell/removeAttributeSuggestions', this.index)
      }
    },

    prevAttributeSetValueId (newVal) {
      const filterPrevAttr = this.prevAttribute && this.attributeSuggestions.filter(a => a.attribute === this.prevAttribute.id)
      const prevSuggestions = filterPrevAttr.length > 0 && filterPrevAttr[0].suggested
      // If previous attr id is not falsy nor zero
      if (newVal && newVal !== 0) {
        // And if the prev attr has suggestions, try to get suggestions for current attr
        if (prevSuggestions && prevSuggestions.length > 0) {
          this.getSuggestions()
        }
        const values = this.attr.values
        const isArrayOfValues = values && values.constructor === Array
        if (!this.isPredictable && isArrayOfValues && values.length === 1) {
          // If attribute is not predictable and there's only one possible value, assign it
          this.assignValues(this.attr.values[0])
        }
      }
    },
  },

  methods: {
    init () {
      this.initializing = true
      const attr = this.attr
      const settedValue = attr.setted_value
      const settedValueId = attr.setted_value_id

      if (this.valueType === 'number_unit') {
        // from setted_value = '2000 km'
        this.value = settedValue && settedValue.split(' ')[0]
        // the unit part from the string
        this.unitId = (settedValue && settedValue.split(' ')[1]) || attr.allowed_units[0].id
      } else {
          // anything else should take these values
          this.valueId = settedValueId
          this.value = settedValue
      }
      // Timeout to prevent valueGroup watcher from executing
      setTimeout(() => {
        this.initializing = false
      }, 250)
    },

    checkInput (e) {
      if (this.required) {
        const targetValue = this.valueType === 'typeahead_list' ? e : e.target.value
        const emptyNumberUnit = this.valueType === 'number_unit' && !this.unitId
        const nonZeroFalsyValue = !this.validValue
        const isAttributeValid = checkAttributeValidity(this.attr.id, targetValue)

        if (nonZeroFalsyValue || emptyNumberUnit || !isAttributeValid) {
          store.dispatch('sell/addAttrWithError', this.attr.id)
        } else {
          store.dispatch('sell/removeAttrWithError', this.attr.id)
        }
      }
    },

    handleHit (e) {
      const filterSug = this.attributeSuggestions.filter(a => a.attribute === this.attr.id)
      const attrSug = filterSug.length > 0 && filterSug[0]
      if (attrSug) {
        attrSug.setted_value = e.name
      }
      this.valueId = e.id
      this.value = e.name
    },

    handleTypeaheadFocus () {
      const currentAttrSuggestions = this.attributeSuggestions.filter(attrSug => attrSug.attribute === this.attr.id)
      const suggestionsSet = currentAttrSuggestions[0] && currentAttrSuggestions[0].suggested && currentAttrSuggestions[0].suggested.length > 0
      if (this.index === 0 && !suggestionsSet || this.isEditingPub && suggestionsSet === undefined)
        this.getSuggestions()
      this.$emit('focus')
    },

    getSuggestions () {
      this.isFetching = true
      store.dispatch('sell/getAttributeSuggestions', { id: this.attr.id, index: this.index } )
        .then(() => {
          const attrSug = this.attributeSuggestions.filter(a => a.attribute === this.attr.id)
          const currentSuggestions = attrSug.length > 0 && attrSug[0].suggested
          if (currentSuggestions && currentSuggestions.length === 0) {
            this.init()
          } else if (currentSuggestions && currentSuggestions.length === 1) {
            // If there's only one possible value, assign it
            this.assignValues(currentSuggestions[0])
          }
        })
        .finally(() => { this.isFetching = false })
    },

    assignValues (val) {
      this.valueId = val.id
      this.value = val.name
      this.initialValue = val.name
    }
  },

  created () {
    // set initial values
    this.init()
    if (this.attr.setted_value) this.initialValue = this.attr.setted_value
  }
}
</script>

<style lang="scss" scoped>
  .form-group {
    & label {
      font-weight: bold;
    }
}
</style>



// WEBPACK FOOTER //
// src/pages/sellPage/AttributeInput.vue